/** Client-side helpers mirroring server apiQueryParams (preview only). */

import { resolveScriptedParamValue, coerceBodyJsonValue } from './apiParamScripting';

let editorRowCounter = 0;

export function createEditorRow(partial = {}) {
  editorRowCounter += 1;
  return {
    id: partial.id || partial.fallbackId || `param-${editorRowCounter}-${Date.now()}`,
    key: partial.key || '',
    value: partial.value != null ? String(partial.value) : '',
    label: partial.label || partial.key || '',
  };
}

function normalizeParamDefsFromSource(apiSource = {}, blockKey, legacyKeys = []) {
  const defs = [];
  const seen = new Set();

  for (const row of Array.isArray(apiSource[blockKey]) ? apiSource[blockKey] : []) {
    const key = String(row?.key || '').trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    defs.push({
      key,
      default: row.default != null ? String(row.default) : '',
      label: row.label != null ? String(row.label) : key,
    });
  }

  const legacy = {};
  for (const lk of legacyKeys) {
    if (apiSource[lk] && typeof apiSource[lk] === 'object') {
      Object.assign(legacy, apiSource[lk]);
    }
  }
  for (const [key, value] of Object.entries(legacy)) {
    const k = String(key || '').trim();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    defs.push({ key: k, default: value != null ? String(value) : '', label: k });
  }

  return defs;
}

export function normalizeParamDefs(apiSource = {}) {
  return normalizeParamDefsFromSource(apiSource, 'query_param_defs', ['query', 'params']);
}

export function normalizeBodyParamDefs(apiSource = {}) {
  return normalizeParamDefsFromSource(apiSource, 'body_param_defs', ['body', 'body_params']);
}

export function normalizeHeaderParamDefs(apiSource = {}) {
  return normalizeParamDefsFromSource(apiSource, 'header_param_defs', ['headers']);
}

export function splitApiOverrides(overrides = {}) {
  if (overrides && typeof overrides === 'object' && !Array.isArray(overrides)) {
    if (overrides.query !== undefined || overrides.body !== undefined || overrides.headers !== undefined) {
      return {
        query: overrides.query && typeof overrides.query === 'object' ? overrides.query : {},
        body: overrides.body && typeof overrides.body === 'object' ? overrides.body : {},
        headers: overrides.headers && typeof overrides.headers === 'object' ? overrides.headers : {},
      };
    }
  }
  const flat = overrides && typeof overrides === 'object' && !Array.isArray(overrides) ? overrides : {};
  return { query: { ...flat }, body: { ...flat }, headers: {} };
}

export function structuredOverridesForPreviewMode(previewMode, rowOverrides = {}) {
  if (previewMode === 'body') return { body: rowOverrides };
  if (previewMode === 'headers') return { headers: rowOverrides };
  return { query: rowOverrides };
}

function defaultsFromParamDefs(defs = []) {
  const out = {};
  for (const row of defs) {
    const key = String(row?.key || '').trim();
    if (!key) continue;
    const val = row.default != null ? String(row.default) : '';
    if (val !== '') out[key] = val;
  }
  return out;
}

function mergeParamOverrides(resolved, overrides = {}, referenceDate = new Date()) {
  for (const [key, value] of Object.entries(overrides || {})) {
    const k = String(key || '').trim();
    if (!k || value === undefined || value === null || value === '') continue;
    resolved[k] = Array.isArray(value) ? value.join(',') : String(value);
  }

  const scripted = {};
  for (const [key, value] of Object.entries(resolved)) {
    scripted[key] = resolveScriptedParamValue(value, referenceDate);
  }
  return scripted;
}

export function resolveApiQueryParams(apiSource = {}, overrides = {}, options = {}) {
  const { query: queryOverrides } = splitApiOverrides(overrides);
  const resolved = defaultsFromParamDefs(normalizeParamDefs(apiSource));
  return mergeParamOverrides(resolved, queryOverrides, options.referenceDate || new Date());
}

export function resolveApiBodyParams(apiSource = {}, overrides = {}, options = {}) {
  const { body: bodyOverrides } = splitApiOverrides(overrides);
  const resolved = defaultsFromParamDefs(normalizeBodyParamDefs(apiSource));
  const merged = mergeParamOverrides(resolved, bodyOverrides, options.referenceDate || new Date());
  return coerceBodyJsonValue(merged);
}

export function resolveApiHeaderParams(apiSource = {}, overrides = {}, options = {}) {
  const { headers: headerOverrides } = splitApiOverrides(overrides);
  const resolved = defaultsFromParamDefs(normalizeHeaderParamDefs(apiSource));
  return mergeParamOverrides(resolved, headerOverrides, options.referenceDate || new Date());
}

export function resolveHttpMethod(apiSource = {}) {
  const method = String(apiSource.http_method || apiSource.method || 'GET').trim().toUpperCase();
  return method === 'POST' ? 'POST' : 'GET';
}

export function formatApiPathPreview(apiSource = {}, overrides = {}, options = {}) {
  const endpoint = String(apiSource.endpoint || '').trim();
  if (!endpoint) return '';

  let pathname = endpoint;
  try {
    pathname = new URL(endpoint).pathname || '/';
  } catch (_) {
    /* keep raw */
  }

  const params = resolveApiQueryParams(apiSource, overrides, options);
  const pairs = Object.entries(params).filter(([, v]) => v != null && v !== '');
  if (!pairs.length) return `${pathname}?`;

  const qs = pairs.map(([k, v]) => {
    const val = typeof v === 'object' ? JSON.stringify(v) : String(v);
    return `${encodeURIComponent(k)}=${encodeURIComponent(val)}`;
  }).join('&');
  return `${pathname}?${qs}&`;
}

export function formatApiHeadersPreview(apiSource = {}, overrides = {}, options = {}) {
  const headers = resolveApiHeaderParams(apiSource, overrides, options);
  if (!headers || !Object.keys(headers).length) return '{}';
  return JSON.stringify(headers, null, 2);
}

export function formatApiBodyPreview(apiSource = {}, overrides = {}, options = {}) {
  const payload = resolveApiBodyParams(apiSource, overrides, options);
  if (!payload || !Object.keys(payload).length) return '{}';
  return JSON.stringify(payload, null, 2);
}

export function editorRowsFromParamDefs(defs = []) {
  return (defs || []).map((row, index) => createEditorRow({
    id: row.id,
    key: row.key,
    value: row.default != null ? String(row.default) : '',
    label: row.label || row.key,
    fallbackId: `saved-${row.key || 'row'}-${index}`,
  }));
}

export function editorRowsFromOverridesObject(obj = {}) {
  return Object.entries(obj || {}).map(([key, value]) => createEditorRow({
    key,
    value: value != null ? String(value) : '',
    label: key,
  }));
}

export function paramDefsFromEditorRows(rows = []) {
  return (rows || [])
    .map((row) => ({
      key: String(row?.key || '').trim(),
      default: row?.value != null ? String(row.value) : '',
      label: String(row?.label || row?.key || '').trim() || String(row?.key || '').trim(),
    }))
    .filter((row) => row.key);
}

export function overridesFromEditorRows(rows = [], defaults = {}) {
  const out = {};
  for (const row of rows || []) {
    const key = String(row?.key || '').trim();
    if (!key) continue;
    const val = row?.value != null ? String(row.value) : '';
    if (val === '') continue;
    if (defaults[key] === val) continue;
    out[key] = val;
  }
  for (const row of rows || []) {
    const key = String(row?.key || '').trim();
    if (!key) continue;
    const val = row?.value != null ? String(row.value) : '';
    if (val !== '' && !(key in defaults)) out[key] = val;
  }
  return out;
}

export function resolvedParamsFromEditorRows(rows = []) {
  const out = {};
  for (const row of rows || []) {
    const key = String(row?.key || '').trim();
    if (!key) continue;
    const val = row?.value != null ? String(row.value) : '';
    if (val !== '') out[key] = val;
  }
  return out;
}

export function rowsToOverridesObject(rows = []) {
  const out = {};
  for (const row of rows || []) {
    const key = String(row?.key || '').trim();
    if (!key) continue;
    const val = row?.value != null ? String(row.value) : '';
    if (val !== '') out[key] = val;
  }
  return out;
}

export function harnessOverridesFromRows({ queryRows = [], bodyRows = [], headerRows = [] } = {}) {
  return {
    query: rowsToOverridesObject(queryRows),
    body: rowsToOverridesObject(bodyRows),
    headers: rowsToOverridesObject(headerRows),
  };
}

function rowsFromOverrideObject(obj = {}) {
  const entries = Object.entries(obj || {});
  return entries.map(([key, value]) => createEditorRow({ key, value: String(value), label: key }));
}

function defaultValuesFromDefs(defs = []) {
  const out = {};
  for (const row of defs || []) {
    const key = String(row?.key || '').trim();
    if (!key) continue;
    const val = row.default != null ? String(row.default) : '';
    if (val !== '') out[key] = val;
  }
  return out;
}

export function harnessDeltaFromRows(apiSource = {}, { queryRows = [], bodyRows = [], headerRows = [] } = {}) {
  return {
    query: overridesFromEditorRows(queryRows, defaultValuesFromDefs(normalizeParamDefs(apiSource))),
    body: overridesFromEditorRows(bodyRows, defaultValuesFromDefs(normalizeBodyParamDefs(apiSource))),
    headers: overridesFromEditorRows(headerRows, defaultValuesFromDefs(normalizeHeaderParamDefs(apiSource))),
  };
}

export function harnessRowsFromGroup(apiSource = {}, stored = null) {
  const defaultQuery = editorRowsFromParamDefs(normalizeParamDefs(apiSource));
  const defaultHeaders = editorRowsFromParamDefs(normalizeHeaderParamDefs(apiSource));
  const defaultBody = editorRowsFromParamDefs(normalizeBodyParamDefs(apiSource));
  const empty = emptyHarnessRows();

  const storedQuery = stored?.query && typeof stored.query === 'object' ? stored.query : null;
  const storedHeaders = stored?.headers && typeof stored.headers === 'object' ? stored.headers : null;
  const storedBody = stored?.body && typeof stored.body === 'object' ? stored.body : null;

  const queryRows = storedQuery
    ? mergeEditorRows(defaultQuery, rowsFromOverrideObject(storedQuery))
    : (defaultQuery.length ? defaultQuery : empty.queryRows);
  const headerRows = storedHeaders
    ? mergeEditorRows(defaultHeaders, rowsFromOverrideObject(storedHeaders))
    : (defaultHeaders.length ? defaultHeaders : empty.headerRows);
  const bodyRows = storedBody
    ? mergeEditorRows(defaultBody, rowsFromOverrideObject(storedBody))
    : (defaultBody.length ? defaultBody : empty.bodyRows);

  return { queryRows, headerRows, bodyRows };
}

export function emptyHarnessRows() {
  return {
    queryRows: [createEditorRow()],
    bodyRows: [createEditorRow()],
    headerRows: [createEditorRow()],
  };
}

export function mergeEditorRows(defaultRows = [], overrideRows = []) {
  const map = new Map();
  for (const row of defaultRows) {
    if (row?.key) map.set(row.key, { ...row });
  }
  for (const row of overrideRows) {
    if (row?.key) {
      const existing = map.get(row.key);
      map.set(row.key, createEditorRow({
        ...(existing || { label: row.key }),
        ...row,
        id: existing?.id || row.id,
      }));
    }
  }
  return [...map.values()];
}
